<template>
  <div class="stay-overview-card">
    <div class="stay-description">
      <div class="stay-description-line box">
        <div class="section-label">
          {{ tc("noums.roomSingular") }} {{ index }}
        </div>
        <div class="overview-item guests">
          <div class="item-label bold-item">
            {{ stay.sitecoreContent.room.title }}
          </div>
          <div
            class="item-value section-label"
            v-html="stayGuestsSummary"
          />
        </div>
      </div>
      <hr>
      <div class="stay-privileges-info box">
        <div class="enhancements-overview-title">
          {{ tc("booking.complementaryPrivilages") }}
        </div>
        <template v-if="showPrivilegesEditButton">
          <a
            href="#"
            class="edit"
            @click.prevent="editStay"
            @keydown.space.prevent="editStay"
          >{{ tc("booking.edit") }}</a>
        </template>
        <template v-else>
          <span class="disclaimer">
            <span class="icon-warning-red" />
            {{
              tc("booking.modifyReservationComplimentaryPrivilegesUnavailable")
            }}
          </span>
        </template>
      </div>
      <hr v-if="enhancementsInRoom.length">
      <div
        v-if="enhancementsInRoom.length"
        class="stay-privileges box"
      >
        <div
          v-for="enhancement in enhancementsInRoom"
          :key="enhancement.sitecoreId"
          class="cart-enhancement"
        >
          <div class="enhancement-title">
            {{ enhancement.enhancement.name }}
          </div>
          <div
            v-if="enhancement.summary"
            class="enhancement-summary section-label"
            v-html="enhancement.summary"
          />
        </div>
      </div>
    </div>
    <hr v-if="addOnsInRoom.length">
    <div
      v-if="addOnsInRoom.length"
      class="stay-add-ons box"
    >
      <div class="stay-add-ons-title">
        {{ addOnsTitle }}
      </div>
      <template>
        <a
          href="#"
          class="edit"
          @click.prevent="editStay"
          @keydown.space.prevent="editStay"
        >{{ tc("booking.edit") }}</a>
      </template>
      <hr>
      <div
        v-for="addOn in addOnsInRoom"
        :key="addOn.sitecoreId"
        class="stay-add-ons-item"
      >
        <div class="stay-add-ons-item-info">
          <div class="stay-add-ons-item-title">
            {{ addOn.addOnName }}
          </div>
          <div class="stay-add-ons-item-price">
            {{ getAddOnPriceLabel(addOn) }}
          </div>
          <div class="stay-add-ons-item-quantity">
            {{ quantityLabel }}: {{ getTotalQuantity(addOn) }}
          </div>
          <template v-if="showAdultChildLabels(addOn)">
            <div v-if="addOn.adultQuantity" class="stay-add-ons-item-quantity">
              {{ adultLabel }}: {{ addOn.adultQuantity }}
            </div>
            <div v-if="addOn.childQuantity" class="stay-add-ons-item-quantity">
              {{ childLabel }}: {{ addOn.childQuantity }}
            </div>
          </template>
          <template v-if="addOn.data.selectedAddOnDates && addOn.data.selectedAddOnDates.length > 1">
            <div class="stay-add-ons-item-dates">
              {{ datesLabel }}
            </div>
            <div v-for="date in addOn.data.selectedAddOnDates" :key="date" class="stay-add-ons-item-date">
              {{ dateFormatted(date) }}
            </div>
          </template>
        </div>
        <div class="stay-add-ons-item-total">
          {{ getTotalPriceLabel(addOn) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex'
  import { formatDateZone } from "@utils/date-helpers";

  export default {
    name: 'PrivilegeOverviewCard',
    props: {
      stay: {
        type: Object,
        required: true,
      },
      index: {
        type: Number,
        required: true,
      },
    },
    data() {
      return {
        showPrivilegesEditButton: true,
      }
    },
    computed: {
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('booking', ['getSummaryDetails']),
      ...mapGetters('cart', [
        'getEnhancementsInRoom',
        'getAddOnsInRoom',
      ]),
      ...mapGetters('currency', ['getDisplayPrice']),
      ...mapGetters('auth', ['enhancementsAllowed', 'isAuthenticated']),
      ...mapState('booking', ['query']),
      ...mapState('modifyReservation', ['modifyBookingPropData']),
      ...mapState('cart', ['validEnhancements', 'cartPending']),
      ...mapState('auth', ['addOnsComponentData']),
      enhancementsInRoom() {
        return this.getEnhancementsInRoom(this.index - 1)
      },
      addOnsInRoom() {
        return this.getAddOnsInRoom(this.index - 1)
      },
      stayGuests() {
        if (this.index === 1) {
          return {
            adultGuestCount: this.query.adultGuestCount,
            childGuestCount: this.query.childGuestCount,
          }
        }
        return this.query.aditionalGuests[this.index - 2]
      },
      stayGuestsSummary() {
        return this.getSummaryDetails({
          customQuery: this.stayGuests,
          separator: ',<br>',
        })
      },
      addOnsTitle() {
        return this.addOnsComponentData?.AddOnsTitle || ""
      },
      datesLabel() {
        return this.addOnsComponentData?.DateLabel || ""
      },
      quantityLabel() {
        return this.addOnsComponentData?.QuantityLabel || ""
      },
      adultLabel() {
        return this.addOnsComponentData?.AdultLabel || ""
      },
      childLabel() {
        return this.addOnsComponentData?.ChildLabel || ""
      },
      editStepIndex() {
        return this.isAuthenticated ? 2 : 1
      },
    },
    watch: {
      cartPending(val) {
        this.updateEditButtonVisibility()
      },
      isAuthenticated(val) {
        if (val) {
          this.showPrivilegesEditButton = true
        }
      },
    },
    mounted() {
      this.updateEditButtonVisibility()
    },
    methods: {
      ...mapActions('booking', ['selectRoom', 'goToStep']),
      async editStay() {
        await this.selectRoom(this.index - 1)
        await this.goToStep(this.editStepIndex)
      },
      updateEditButtonVisibility() {
        this.showPrivilegesEditButton = !!this.validEnhancements[this.index - 1]?.length || !this.isAuthenticated
      },
      getAddOnPriceLabel(addOn) {
        const addOnPrice = addOn?.priceWithTaxes[0]
        const priceFormatted = this.getDisplayPrice({ price: addOnPrice })
        const addOnTypeLabel = this.addOnsComponentData?.ServicePricingType[addOn.addOn.servicePricingType]
        return addOn.airportAddOn ? priceFormatted : `${priceFormatted}/${addOnTypeLabel}`
      },
      getTotalPriceLabel(addOn) {
        const totalFormatted = this.getDisplayPrice({ price: Number(addOn.total) })
        return `${totalFormatted}`
      },
      dateFormatted(date) {
        return formatDateZone(date, null, "ddd, D MMM, YYYY")
      },
      getTotalQuantity(addOn) {
        if(addOn.airportAddOn || addOn.servicePricingType === 'per-stay' || addOn.servicePricingType === 'per-night'){
          return addOn.quantity
        } else {
          return Number(addOn.adultQuantity) + Number(addOn.childQuantity)
        }
      },
      showAdultChildLabels(addOn) {
        return !(addOn.airportAddOn || addOn.servicePricingType === 'per-stay' || addOn.servicePricingType === 'per-night')
      }
    },
  }
</script>
